// The builder report registry — composed, human-reviewed RAMS data. The 5
// launch reports were ported from the content-engine curated graph; the
// library-expansion reports (2026-06-06) were authored per
// docs/builder-report-authoring-spec.md from the templateSpecific seeds +
// rams-knowledge bank. The product owns its copy (no cross-factory import).

import type { CategoryKey } from "./library";
import { GENERATED_ACTIVE_SLUGS } from "./reportLinks";
import generatedBundle from "../content/reports/generated-reports.json";

// Launch reports (content-engine port)
import boiler from "./reports/ph-boiler-install.json";
import consumerUnit from "./reports/el-consumer-unit.json";
import pitchedRecover from "./reports/rf-pitched-recover.json";
import scaffoldErection from "./reports/sc-erection.json";
import excavation from "./reports/gw-excavation.json";
// Work at height
import wahGeneral from "./reports/wah-general.json";
import wahMobileTower from "./reports/wah-mobile-tower.json";
import wahMewp from "./reports/wah-mewp.json";
import wahLadders from "./reports/wah-ladders.json";
import wahFragileRoof from "./reports/wah-fragile-roof.json";
import wahGutter from "./reports/wah-gutter.json";
import wahFallArrest from "./reports/wah-fall-arrest.json";
import wahEdgeProtection from "./reports/wah-edge-protection.json";
// Electrical
import elecInstallation from "./reports/elec-installation.json";
import elecLiveWorking from "./reports/elec-live-working.json";
import elecTempSupply from "./reports/elec-temp-supply.json";
import elecOverheadLines from "./reports/elec-overhead-lines.json";
// Groundworks
import groundTrenching from "./reports/ground-trenching.json";
import groundConfinedSpace from "./reports/ground-confined-space.json";
import groundDrainage from "./reports/ground-drainage.json";
import groundUndergroundServices from "./reports/ground-underground-services.json";
// Lifting & plant
import liftCrane from "./reports/lift-crane.json";
import liftTelehandler from "./reports/lift-telehandler.json";
import liftExcavator from "./reports/lift-excavator.json";
import liftDumper from "./reports/lift-dumper.json";
import liftTrafficMgmt from "./reports/lift-traffic-mgmt.json";
import liftManualHandling from "./reports/lift-manual-handling.json";
// Hot works
import hotWelding from "./reports/hot-welding.json";
import hotCuttingGrinding from "./reports/hot-cutting-grinding.json";
import hotRoofingTorch from "./reports/hot-roofing-torch.json";
// COSHH & substances
import coshhGeneral from "./reports/coshh-general.json";
import coshhSilica from "./reports/coshh-silica.json";
import coshhCement from "./reports/coshh-cement.json";
import coshhSolvents from "./reports/coshh-solvents.json";
import coshhLead from "./reports/coshh-lead.json";
// Demolition & strip-out
import demoSoftStrip from "./reports/demo-soft-strip.json";
import demoStructural from "./reports/demo-structural.json";
import demoAsbestosAwareness from "./reports/demo-asbestos-awareness.json";
// Trades & finishing
import tradeBricklaying from "./reports/trade-bricklaying.json";
import tradeSteelFixing from "./reports/trade-steel-fixing.json";
import tradeFormwork from "./reports/trade-formwork.json";
import tradeConcreting from "./reports/trade-concreting.json";
import tradeJoinery from "./reports/trade-joinery.json";
import tradePlastering from "./reports/trade-plastering.json";
import tradePainting from "./reports/trade-painting.json";
import tradeFlooring from "./reports/trade-flooring.json";
import tradeTiling from "./reports/trade-tiling.json";
// Site & general
import siteSetup from "./reports/site-setup.json";
import siteOccupied from "./reports/site-occupied.json";
import siteLoneWorking from "./reports/site-lone-working.json";
import siteNoiseHavs from "./reports/site-noise-havs.json";
import siteTempWorks from "./reports/site-temp-works.json";

export type RiskPair = { likelihood: number; severity: number };

export type ReportHazard = {
  hazard: string;
  hazardId: string;
  whoIsAtRisk: string[];
  initialRisk: RiskPair;
  controls: string[];
  residualRisk: RiskPair;
  citation?: string | null;
};

export type ReportSiteField = { key: string; label: string; required: boolean };

export type RamsReport = {
  id: string;
  title: string;
  category: string;
  trade: string;
  task: string;
  version: number;
  /** When the underlying knowledge library was last reviewed (ISO date). */
  lastReviewed?: string;
  siteFields: ReportSiteField[];
  scopeOfWorks: string;
  sequenceOfWorks: { order: number; description: string }[];
  hazards: ReportHazard[];
  ppe: string[];
  equipment: string[];
  competence: { requirement: string; schemeIsNotStatute?: boolean }[];
  emergencyRescue: string;
  permitsRequired: string[];
  substances: string[];
  citations: { claim: string; source: string }[];
};

export type ReportEntry = {
  slug: string;
  /** Short picker label (the task). */
  label: string;
  trade: string;
  /** Library category the report groups under in the builder and /templates/. */
  category: CategoryKey;
  data: RamsReport;
};

/** Shared document-control + emergency fields every report needs (the two
 *  PC rejection triggers the ported JSONs don't cover: missing contractor
 *  identity / revision, and generic emergency arrangements with no named
 *  first aider or assembly point). Injected here so the report JSONs stay
 *  exactly as authored and every report picks the fields up automatically. */
function withSharedFields(report: RamsReport): RamsReport {
  const fields: ReportSiteField[] = [];
  for (const f of report.siteFields) {
    // The single mushy welfare field becomes the specific items a reviewer
    // checks: named first aider, assembly point, nearest A&E, welfare.
    if (f.key === "welfareFirstAid") {
      fields.push(
        { key: "firstAider", label: "First aider (name)", required: true },
        { key: "assemblyPoint", label: "Emergency assembly point", required: true },
        { key: "nearestAE", label: "Nearest A&E", required: false },
        { key: "welfareArrangements", label: "Welfare arrangements", required: false },
      );
      continue;
    }
    fields.push(f);
    if (f.key === "client") {
      fields.push(
        { key: "companyName", label: "Your company (contractor)", required: true },
        { key: "companyContact", label: "Company contact (phone / email)", required: false },
      );
    }
    if (f.key === "reviewDate") {
      fields.push({ key: "revision", label: "Revision", required: false });
    }
  }
  return { ...report, siteFields: fields };
}

const entry = (slug: string, label: string, trade: string, category: CategoryKey, data: unknown): ReportEntry => ({
  slug,
  label,
  trade,
  category,
  data: withSharedFields(data as RamsReport),
});

/** Every library template's report plus the two trade flagships (boiler,
 *  consumer unit). Ordered by library category; flagship launch reports first
 *  within their category. */
const HAND_REPORTS: ReportEntry[] = [
  // Work at height
  entry("pitched-roof-recover", "Pitched roof re-cover", "Roofing", "work-at-height", pitchedRecover),
  entry("scaffold-erection", "Scaffold erection", "Scaffolding", "work-at-height", scaffoldErection),
  entry("working-at-height", "Working at height (general)", "General", "work-at-height", wahGeneral),
  entry("mobile-tower-scaffold", "Mobile tower scaffold", "Access", "work-at-height", wahMobileTower),
  entry("mewp-cherry-picker", "MEWP / cherry picker", "Access", "work-at-height", wahMewp),
  entry("ladders-stepladders", "Ladders & stepladders", "Access", "work-at-height", wahLadders),
  entry("fragile-roof-work", "Fragile roof work", "Roofing", "work-at-height", wahFragileRoof),
  entry("gutter-cleaning", "Gutter cleaning at height", "Maintenance", "work-at-height", wahGutter),
  entry("fall-arrest-harness", "Fall arrest / harness use", "Access", "work-at-height", wahFallArrest),
  entry("edge-protection", "Edge protection installation", "Scaffolding", "work-at-height", wahEdgeProtection),
  // Electrical
  entry("consumer-unit-replacement", "Consumer unit replacement", "Electrical", "electrical", consumerUnit),
  entry("electrical-installation", "Electrical installation", "Electrical", "electrical", elecInstallation),
  entry("working-near-live-services", "Working near live services", "Electrical", "electrical", elecLiveWorking),
  entry("temporary-site-electrics", "Temporary site electrics", "Electrical", "electrical", elecTempSupply),
  entry("overhead-power-lines", "Work near overhead power lines", "Groundworks", "electrical", elecOverheadLines),
  // Groundworks & excavation
  entry("excavation", "Excavation", "Groundworks", "groundworks", excavation),
  entry("trenching-shoring", "Trenching & shoring", "Groundworks", "groundworks", groundTrenching),
  entry("confined-space-entry", "Confined space entry", "Groundworks", "groundworks", groundConfinedSpace),
  entry("drainage-installation", "Drainage installation", "Groundworks", "groundworks", groundDrainage),
  entry("underground-services", "Underground services", "Groundworks", "groundworks", groundUndergroundServices),
  // Lifting & plant
  entry("crane-lifting-operations", "Crane lifting operations", "Lifting", "lifting-plant", liftCrane),
  entry("telehandler", "Telehandler operation", "Plant", "lifting-plant", liftTelehandler),
  entry("excavator-operation", "360 excavator operation", "Plant", "lifting-plant", liftExcavator),
  entry("site-dumper", "Site dumper operation", "Plant", "lifting-plant", liftDumper),
  entry("site-traffic-management", "Site traffic management", "Site", "lifting-plant", liftTrafficMgmt),
  entry("manual-handling", "Manual handling", "General", "lifting-plant", liftManualHandling),
  // Hot works
  entry("boiler-installation", "Boiler installation", "Plumbing & Heating", "hot-works", boiler),
  entry("welding", "Welding", "Mechanical", "hot-works", hotWelding),
  entry("cutting-grinding", "Cutting & grinding", "General", "hot-works", hotCuttingGrinding),
  entry("torch-on-roofing", "Torch-on felt roofing", "Roofing", "hot-works", hotRoofingTorch),
  // COSHH & substances
  entry("coshh-assessment", "COSHH assessment", "General", "coshh-substances", coshhGeneral),
  entry("silica-dust", "Silica dust (RCS)", "General", "coshh-substances", coshhSilica),
  entry("cement-concrete", "Cement & wet concrete", "Groundworks", "coshh-substances", coshhCement),
  entry("solvents-paints", "Solvents & paints", "Finishing", "coshh-substances", coshhSolvents),
  entry("lead-paint", "Lead paint work", "Finishing", "coshh-substances", coshhLead),
  // Demolition & strip-out
  entry("soft-strip", "Soft strip-out", "Demolition", "demolition-strip", demoSoftStrip),
  entry("structural-demolition", "Structural demolition", "Demolition", "demolition-strip", demoStructural),
  entry("asbestos-awareness", "Asbestos awareness (non-licensed)", "Demolition", "demolition-strip", demoAsbestosAwareness),
  // Trades & finishing
  entry("bricklaying-blockwork", "Bricklaying & blockwork", "Bricklaying", "trades-finishing", tradeBricklaying),
  entry("steel-fixing", "Steel fixing", "Groundworks", "trades-finishing", tradeSteelFixing),
  entry("formwork-shuttering", "Formwork & shuttering", "Groundworks", "trades-finishing", tradeFormwork),
  entry("concrete-pour", "Concrete pour", "Groundworks", "trades-finishing", tradeConcreting),
  entry("carpentry-joinery", "Carpentry & joinery", "Carpentry", "trades-finishing", tradeJoinery),
  entry("plastering", "Plastering", "Plastering", "trades-finishing", tradePlastering),
  entry("painting-decorating", "Painting & decorating", "Decorating", "trades-finishing", tradePainting),
  entry("floor-laying", "Floor laying", "Flooring", "trades-finishing", tradeFlooring),
  entry("tiling", "Wall & floor tiling", "Tiling", "trades-finishing", tradeTiling),
  // Site & general
  entry("site-setup-welfare", "Site setup & welfare", "Site", "site-general", siteSetup),
  entry("occupied-premises", "Working in occupied premises", "Site", "site-general", siteOccupied),
  entry("lone-working", "Lone working", "General", "site-general", siteLoneWorking),
  entry("noise-vibration", "Noise & vibration (HAVS)", "General", "site-general", siteNoiseHavs),
  entry("temporary-works", "Temporary works", "Site", "site-general", siteTempWorks),
];

// Generated builder reports (content-engine `export:reports`) — one per library
// template the hand-authored set doesn't cover, so every template is buildable.
// Same entry()/withSharedFields wrapping; filtered to the active generated slugs.
const GENERATED_ENTRIES: ReportEntry[] = (generatedBundle as {
  reports: { slug: string; label: string; trade: string; category: string; data: unknown }[];
}).reports
  .filter((r) => GENERATED_ACTIVE_SLUGS.has(r.slug))
  .map((r) => entry(r.slug, r.label, r.trade, r.category as CategoryKey, r.data));

export const REPORTS: ReportEntry[] = [...HAND_REPORTS, ...GENERATED_ENTRIES];

export function getReport(slug: string): ReportEntry | undefined {
  return REPORTS.find((r) => r.slug === slug);
}

/** Split the composed emergency-rescue wall of text into labelled scenarios
 *  ("Gas leak suspected: …", "Electric shock: …") without lookbehind (Safari). */
export function splitEmergency(text: string): { heading: string | null; body: string }[] {
  const parts = text.split(/\.\s+(?=[A-Z][^.:]{2,60}:\s)/);
  return parts.map((raw, i) => {
    const p = i < parts.length - 1 && !raw.endsWith(".") ? `${raw}.` : raw;
    const m = p.match(/^([A-Z][^.:]{2,60}):\s+([\s\S]*)$/);
    return m ? { heading: m[1], body: m[2] } : { heading: null, body: p };
  });
}
